import { useEffect, useLayoutEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Box, Typography, useMediaQuery } from '@mui/material';
import { IDictionaryItem } from 'api/types';
import { DIAGNOSTIC_SUBJECT_ALL_CODE } from 'const';
import { useAppSelector } from 'hooks';
import { placeholderCharacter } from 'images';
import NoDataPlug from 'portfolio3/components/common/NoDataPlug';
import { WidgetContainer } from 'portfolio3/components/common/WidgetContainer';
import { emitYMEvent } from 'portfolio3/features/yandexMetrika';
import { commonTheme } from 'portfolio3/styles/theme';
import { Select } from 'portfolio3/ui-kit/selects';
import { IController } from 'portfolio3/ui-kit/types';
import { formatDate, isCurrentLearningYear, isDefined } from 'utils';

import { PESONAL_DIAGNOSTIC_SEARCH_PARAM_KEY } from '../../const';
import PersonalBestResultDiagram from './PersonalBestResultDiagram';
import PersonalDiagnosticGeneralMobileFilter from './PersonalDiagnosticGeneralMobileFilter';
import PersonalDiagnosticList from './PersonalDiagnosticList';
import PersonalDiagnosticListMobileFilter from './PersonalDiagnosticListMobileFilter';
import { getFilteredDiagnostics, getLearningYearOptions, getSubjectOptions } from './utils';

type PersonalDiagnosticsContainerProps = {
  searchStringId: string | null;
};
const PersonalDiagnosticsContainer = ({ searchStringId }: PersonalDiagnosticsContainerProps) => {
  const isSmallScreen = useMediaQuery(commonTheme.breakpoints.down('commonSm'));

  const { content: diagnosticGroups, loading } = useAppSelector((state) => state.personalDiagnostics);

  const [currentLearningYear, setCurrentLearningYear] = useState<IDictionaryItem | null>(null);
  const [currentSubject, setCurrentSubject] = useState<IDictionaryItem | null>(null);

  const currentDiagnosticsGroup = diagnosticGroups.find((group) => group.learningYear === currentLearningYear?.value);
  const bestResult = currentDiagnosticsGroup?.bestResult;

  const filteredDiagnostics = getFilteredDiagnostics(
    currentDiagnosticsGroup?.diagnostics ?? [],
    currentSubject?.code === DIAGNOSTIC_SUBJECT_ALL_CODE ? null : currentSubject?.value ?? null,
  );
  const learningYearsOptions = getLearningYearOptions(diagnosticGroups);
  const subjectOptions = getSubjectOptions(currentDiagnosticsGroup?.diagnostics ?? []);
  const history = useHistory();
  const location = useLocation();

  // Первоначальная установка учебного года
  useEffect(() => {
    if (!isDefined(currentLearningYear) && learningYearsOptions.length > 0 && !searchStringId) {
      setCurrentLearningYear(learningYearsOptions[0] ?? null);
    }
  }, [currentLearningYear, learningYearsOptions, searchStringId]);

  // Установка первого предмета, когда меняется учебный год
  useLayoutEffect(() => {
    setCurrentSubject(subjectOptions[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDiagnosticsGroup?.learningYear]);

  // Если в search параметрах задан searchStringId, то задаем соответсвующий learningYear
  useEffect(() => {
    if (searchStringId && !loading && diagnosticGroups.length) {
      const newLearningYear = diagnosticGroups.reduce<IDictionaryItem | null>((acc, group) => {
        const diagnostic = group.diagnostics.find((d) => d.recordId === searchStringId);

        if (diagnostic) {
          const learningYearsOptions = getLearningYearOptions(diagnosticGroups);
          const year = learningYearsOptions.find((opt) => opt.value === group.learningYear);
          if (year) {
            return year;
          }
        }

        return acc;
      }, null);

      if (newLearningYear) {
        setCurrentLearningYear(newLearningYear);
      } else {
        const params = new URLSearchParams(location.search);
        params.delete(PESONAL_DIAGNOSTIC_SEARCH_PARAM_KEY);
        history.replace({
          pathname: location.pathname,
          search: params.toString(),
          hash: location.hash,
        });
      }
    }
  }, [searchStringId, diagnosticGroups, loading]);

  const getLearningYearRenderValue = (option: IDictionaryItem) => {
    const isCurrentPeriod = isCurrentLearningYear(option.value || '');
    const appendedText = isCurrentPeriod ? ' (текущий)' : '';
    return `${option.value} уч. год` + appendedText;
  };

  const selectPeriodsRenderValue = (code: string | undefined): string => {
    const option = learningYearsOptions?.find((period) => period.code === Number(code));
    return option ? getLearningYearRenderValue(option) : '';
  };

  const learningYearController: IController<string | undefined> = {
    handleChange(code) {
      emitYMEvent({
        type: 'yearStudySwitching',
        payload: {
          sectionType: 'individual',
        },
      });
      const learningYearOption = learningYearsOptions.find((option) => option.code === Number(code));
      if (learningYearOption) {
        setCurrentLearningYear(learningYearOption);
      }
    },
  };

  const subjectController: IController<string | undefined> = {
    handleChange(code) {
      const subjectOption = subjectOptions.find((option) => option.code === Number(code));
      if (subjectOption) {
        setCurrentSubject(subjectOption);
        emitYMEvent({
          type: 'diagnosticItemsFiltering',
          payload: {
            sectionType: 'individual',
          },
        });
      }
    },
  };

  const generalFilterElement = (
    <>
      {isSmallScreen && (
        <PersonalDiagnosticGeneralMobileFilter
          currentLearningYear={currentLearningYear}
          learningYearOptions={learningYearsOptions}
          setCurrentLearningYear={setCurrentLearningYear}
          getLearningYearLabel={getLearningYearRenderValue}
        />
      )}
      {!isSmallScreen && (
        <Box sx={{ width: '272px' }}>
          <Select
            inputRenderMode="fixed"
            inputSize="medium"
            value={currentLearningYear?.code}
            options={learningYearsOptions}
            controller={learningYearController}
            renderValue={selectPeriodsRenderValue}
          />
        </Box>
      )}
    </>
  );

  const listFilterElement = (
    <>
      {isSmallScreen && (
        <Box width="100%" display="flex" gap="8px" justifyContent="space-between">
          <Typography variant="Paragraph LG/Bold">Пройденные диагностики</Typography>
          <PersonalDiagnosticListMobileFilter
            currentSubject={currentSubject}
            subjectOptions={subjectOptions}
            setCurrentSubject={setCurrentSubject}
          />
        </Box>
      )}
      {!isSmallScreen && (
        <Box sx={{ width: '272px' }}>
          <Select
            inputRenderMode="fixed"
            inputSize="medium"
            value={currentSubject?.code}
            options={subjectOptions}
            controller={subjectController}
          />
        </Box>
      )}
    </>
  );

  const bestResultElement = bestResult && (
    <>
      <WidgetContainer.Divider />
      <WidgetContainer.Padding>
        <PersonalBestResultDiagram
          score={bestResult.bestResultValue}
          scorePercent={bestResult.bestResultPercent}
          date={formatDate(bestResult.bestResultEventDate)}
          subject={bestResult.bestResultSubject}
        />
      </WidgetContainer.Padding>
    </>
  );

  const hasData = isDefined(diagnosticGroups) && diagnosticGroups.length > 0;

  if (!hasData || loading) {
    return (
      <WidgetContainer.Padding>
        <NoDataPlug
          image={placeholderCharacter}
          title="Пройденных индивидуальных диагностик пока нет"
          subtitle="Как только они появятся, информация о них отобразится здесь"
          loading={loading}
        />
      </WidgetContainer.Padding>
    );
  }

  return (
    <>
      <WidgetContainer.Padding sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {generalFilterElement}
      </WidgetContainer.Padding>

      {bestResultElement}

      <WidgetContainer.Divider />

      <WidgetContainer.Padding sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {listFilterElement}
      </WidgetContainer.Padding>
      <WidgetContainer.Padding sx={{ marginTop: '16px' }}>
        <PersonalDiagnosticList diagnostics={filteredDiagnostics} />
      </WidgetContainer.Padding>
    </>
  );
};

export default PersonalDiagnosticsContainer;
